import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useInlineTextStyleIcon } from "@/components/doc/right-sidebar/text-style/inline-text-style/useInlineTextStyleIcon";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { INLINE_TEXT_STYLES } from "@/components/tiptap/inline-text-style/inlineTextStyles";
import { twMerge } from "@/utils/twMerge";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { DocumentModel } from "@/models/DocumentModel";

export const InlineTextStyleDropdown = observer(function InlineTextStyleDropdown({
  doc,
}: {
  doc: DocumentModel;
}) {
  const t = useTranslations();
  const { tiptap, editorState } = doc.editor;
  const { textStyleIcon, isActive } = useInlineTextStyleIcon(doc);

  if (!tiptap) {
    return null;
  }

  const handleOnClick = () => {
    if (!isActive) {
      tiptap.chain().focus().toggleBold().run();
      return;
    }

    tiptap.chain().focus().clearFormatting().run();
  };

  const items = INLINE_TEXT_STYLES.filter((style) => style.key !== "inlineCode").map((style) => ({
    title: t(`suggestions.inlineTextStyle.${style.key}`),
    icon: (
      <Icon
        name={style.icon}
        className={twMerge(style.isActive(doc) && "[--color-icon-main:var(--color-icon-active)]")}
      />
    ),
    hotkey: style.hotkey,
    active: style.isActive(doc),
    disabled: style.isDisabled(doc),
    onClick: () => style.apply(doc),
  })) satisfies DropdownItem[];

  return (
    <Sidebar.ComboButton
      disabled={editorState.isCode}
      title={
        isActive ? t("suggestions.inlineTextStyle.clear") : t("suggestions.inlineTextStyle.bold")
      }
      hotkey={isActive ? undefined : "bold"}
      icon={
        <Icon
          name={textStyleIcon}
          className={twMerge(isActive && "[--color-icon-main:var(--color-icon-active)]")}
        />
      }
      triggerProps={{
        "aria-label": t("sidebar.textStyle.more"),
      }}
      hintKey="textStyle"
      onClick={handleOnClick}
      items={items}
      disableShifting
    />
  );
});
